import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { messagesApi } from "@/api/messaging";
import {
  pausePollingOnSessionExpiry,
  retryUnlessSessionExpired,
} from "@/api/polling";
import { CountBadge } from "@/components/ui/CountBadge";
import { cn } from "@/design-system/lib/utils";

/**
 * Internal Messaging v1.0 §8 — top-nav unread badge. Polls
 * GET /api/messages/unread-count every 30s while focused; pauses on tab
 * hide. Mirrors the count into document.title so users with the app in a
 * background tab see "(N) Phoenix Capital" without switching.
 *
 * Critical: in v1 there is NO email or push notification, so this badge is
 * the only cue a user has of new messages — keep it unmistakable.
 */
export function MessagesBadge({ className }: { className?: string }) {
  const unread = useQuery({
    queryKey: ["messaging", "unread"],
    queryFn: () => messagesApi.unreadCount(),
    // Stop polling once the session expires — an unattended tab whose cookie
    // lapsed would otherwise 401 every 30s forever (the "401 polling" log spam).
    refetchInterval: pausePollingOnSessionExpiry(30_000),
    retry: retryUnlessSessionExpired,
    // The 30s poll is the freshness mechanism — no extra refetch on every
    // tab focus (perf PR; the global default is also focus-off).
    staleTime: 30_000,
  });

  const count = unread.data ?? 0;
  const originalTitle = useRef<string | null>(null);

  useEffect(() => {
    if (originalTitle.current === null) {
      // Strip any leading "(N) " left by an earlier mount so we don't stack.
      originalTitle.current = document.title.replace(/^\(\d+\+?\)\s*/, "");
    }
    const base = originalTitle.current;
    document.title = count > 0
      ? `(${count > 99 ? "99+" : count}) ${base}`
      : base;
    return () => {
      if (originalTitle.current !== null) document.title = originalTitle.current;
    };
  }, [count]);

  return (
    <Link
      to="/messages"
      aria-label={count > 0 ? `${count} unread messages` : "Messages"}
      className={cn(
        "relative inline-flex items-center justify-center h-10 w-10 rounded-md",
        "text-white-pure/80 hover:text-white-pure hover:bg-white-pure/5",
        "focus-visible:outline-none focus-visible:shadow-goldRing",
        className,
      )}
    >
      <MessageSquare size={18} strokeWidth={1.5} aria-hidden />
      <CountBadge
        count={count}
        tone="error"
        size="sm"
        className="absolute top-[6px] right-[6px]"
      />
    </Link>
  );
}
